import type { TFunction } from 'i18next';
import { hasFullCrew, type ClientRequestStatus } from '@ubus/shared';
import type { Trip } from '@ubus/shared';

type TripStatusInput = Pick<
  Trip,
  'status' | 'source' | 'assignment_status' | 'vehicle_id' | 'driver_id'
>;

export function translateClientRequestStatus(t: TFunction, status: ClientRequestStatus): string {
  return t(`common.status.request.${status}`);
}

export function translateClientTripStatus(t: TFunction, trip: TripStatusInput): string {
  if (
    trip.source === 'platform' &&
    trip.assignment_status === 'pending_owner' &&
    trip.status === 'scheduled'
  ) {
    return t('common.status.trip.awaitingFleetOwner');
  }
  if (trip.source === 'platform' && trip.assignment_status === 'rejected') {
    return t('common.status.trip.reassigning');
  }
  if (trip.status === 'scheduled' && trip.vehicle_id && !trip.driver_id) {
    return t('common.status.trip.vehicleAssignedDriverPending');
  }
  if (trip.status === 'dispatched' && hasFullCrew(trip)) {
    return t('common.status.trip.crewAssigned');
  }
  if (trip.status === 'dispatched' && trip.vehicle_id && !trip.driver_id) {
    return t('common.status.trip.vehicleAssignedDriverPending');
  }
  const keyMap: Record<string, string> = {
    scheduled: 'scheduled',
    dispatched: 'dispatched',
    in_progress: 'inProgress',
    completed: 'completed',
    cancelled: 'cancelled',
  };
  const key = keyMap[trip.status];
  return key ? t(`common.status.trip.${key}`) : trip.status;
}

export function translateTripStatusFilter(t: TFunction, status: string): string {
  if (status === 'all') return t('common.allStatuses');
  const keyMap: Record<string, string> = {
    scheduled: 'scheduled',
    dispatched: 'dispatched',
    in_progress: 'inProgress',
    completed: 'completed',
    cancelled: 'cancelled',
  };
  const key = keyMap[status];
  return key ? t(`common.status.trip.${key}`) : status;
}

export function translateSosEventType(t: TFunction, eventType: string): string {
  const keyMap: Record<string, string> = {
    'Vehicle Breakdown': 'vehicleBreakdown',
    'Traffic Accident': 'trafficAccident',
    'Passenger Emergency': 'passengerEmergency',
    'Vehicle Not Appearing': 'vehicleNotAppearing',
    Other: 'other',
  };
  const key = keyMap[eventType];
  return key ? t(`sos.eventTypes.${key}`) : eventType;
}
